import OfficeSelect from '@/components/OfficeSelect';
import {
  useCreateOvertimePolicyMutation,
  useUpdateOvertimePolicyMutation,
} from '@/app/features/control-panel/policies/policies.api';
import { useGetSalaryStructureQuery } from '@/app/features/employee/emp-payroll';
import CustomDrawer from '@/components/CustomDrawer';
import type { CustomModalProps } from '@/components/CustomModal';
import WithLoader from '@/components/WithLoader';
import { ChevronDownIcon, Clock01Icon, InfoIcon } from '@/icons';
import { getErrorMessage } from '@/lib/error';
import { toast } from '@/lib/toast';
import { hoursToMinutes, minutesToHours } from '@/utils';
import { snakeToCamel } from '@/utils/string.utils';
import {
  Button,
  Divider,
  Flex,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Switch,
  Tooltip,
} from 'antd';
import { useEffect } from 'react';

type Props = CustomModalProps & { id?: string; data?: any; type?: string };
const ManageOvertimePolicy = ({ open, id, handleClose, data, type }: Props) => {
  const [form] = Form.useForm();

  // Watched values
  const amountType = Form.useWatch('type', form);

  // Get salary structure
  const { data: salaryStructureData } = useGetSalaryStructureQuery(null);

  // Create overview mutations
  const [createOvertimePolicy, { isLoading: isCreating }] =
    useCreateOvertimePolicyMutation();
  // Update overview mutations
  const [updateOvertimePolicy, { isLoading: isUpdating }] =
    useUpdateOvertimePolicyMutation();

  const handleSubmit = async (values: any) => {
    const {
      type,
      status,
      max_overtime_minutes,
      overtime_rate_per_hour_percent_of: rateOf,
      ...rest
    } = values || {};

    if (type === 'percentage' && !rateOf) {
      form.setFields([
        {
          name: 'overtime_rate_per_hour_percent_of',
          errors: ['Please select rate of'],
        },
      ]);
      return;
    }

    const formData = {
      ...rest,
      status: status ? 'active' : 'inactive',
      max_overtime_minutes: hoursToMinutes(max_overtime_minutes),
      overtime_rate_per_hour_percent_of:
        type === 'fixed_amount' ? null : rateOf,
    };

    try {
      let response;
      if (id) {
        response = await updateOvertimePolicy({ id, data: formData }).unwrap();
      } else {
        response = await createOvertimePolicy(formData).unwrap();
      }

      // Check if API response has success: false
      if (response && response.success === false) {
        const message = getErrorMessage(response);
        toast(
          'error',
          message || `Failed to ${id ? 'update' : 'create'} overtime policy`,
        );
        return;
      }

      toast(
        'success',
        `Overtime policy ${id ? 'updated' : 'created'} successfully`,
      );
      handleClose();
      form.resetFields();
    } catch (error: any) {
      const message = getErrorMessage(error);
      toast(
        'error',
        message || `Failed to ${id ? 'update' : 'create'} overtime policy`,
      );
    }
  };

  useEffect(() => {
    if (data) {
      const isFixedAmount = !data.overtime_rate_per_hour_percent;

      form.setFieldsValue({
        ...data,
        status: data.status === 'active',
        max_overtime_minutes: minutesToHours(data.max_overtime_minutes),
        overtime_rate_per_hour_percent_of: !isFixedAmount
          ? data.overtime_rate_per_hour_percent_of
          : null,
        type: isFixedAmount ? 'fixed_amount' : 'percentage',
      });
    }
  }, [data, form]);

  useEffect(() => {
    if (amountType === 'fixed_amount') {
      // form.setFieldValue('overtime_rate_per_hour_percent', 0);
      form.resetFields([
        'overtime_rate_per_hour_percent_of',
        'overtime_rate_per_hour_percent',
      ]);
    }
    if (amountType === 'percentage') {
      form.resetFields(['per_hour_fixed_amount_bdt']);
    }
  }, [amountType, form]);
  return (
    <CustomDrawer
      width={850}
      open={open}
      onClose={() => {
        handleClose();
        form.resetFields();
      }}
      title={'Overtime Policy'}
    >
      <WithLoader isLoading={false}
        className="rounded-lg"
      >
        <Form
          form={form}
          size="large"
          layout="vertical"
          disabled={type === 'VIEW'}
          className="custom-form space-y-6"
          onFinish={handleSubmit}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
            <Form.Item
              name={'type'}
              initialValue={'fixed_amount'}
              hidden
              noStyle
            >
              <input />
            </Form.Item>
            <Form.Item
              label="Policy Name"
              name="policy_name"
              rules={[{ required: true, message: 'Please enter policy name' }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label="Office Type (Head / Branch / Site)"
              name="office_id"
              rules={[{ required: true, message: 'Please select office type' }]}
            >
              <OfficeSelect placeholder="Select" />
            </Form.Item>
            <Form.Item
              label={'Max Overtime Hour'}
              name={'max_overtime_minutes'}
            >
              <InputNumber
                min={0}
                max={24}
                precision={0}
                controls={false}
                placeholder="e.g 12"
                className="w-full"
                suffix={<Clock01Icon size={18} />}
              />
            </Form.Item>
            <div />
            <Form.Item
              className="label-full"
              name={'per_hour_fixed_amount_bdt'}
              label={
                <Flex justify="space-between" className="!w-full grow">
                  <span>Per Hour Fixed Amount</span>
                  <Switch
                    className="custom-switch"
                    checked={amountType === 'fixed_amount'}
                    onChange={(checked) => {
                      if (checked) {
                        form.setFieldValue('type', 'fixed_amount');
                      }
                    }}
                  />
                </Flex>
              }
            >
              <InputNumber
                min={amountType === 'fixed_amount' ? 1 : 0}
                prefix="BDT"
                controls={false}
                precision={0}
                className="w-full"
                disabled={amountType !== 'fixed_amount' || type === 'VIEW'}
              />
            </Form.Item>
            <Form.Item
              className="label-full"
              label={
                <Flex justify="space-between" className="!w-full grow">
                  <Flex align="center" gap={6}>
                    <span className="block">Over Time Rate Per Hour</span>

                    <Tooltip
                      title={
                        <div className="bg-theme-slate-800 -mx-2 -my-1.5 p-2 rounded-md text-left !border !border-theme-slate-700">
                          <ul className="space-y-1 list-disc ml-4">
                            <li>If basic salary = 8000</li>
                            <li>This month overtime in hour = 5 hour</li>
                            <li>Multiplier setting = 2x of basic salary</li>
                          </ul>

                          <Divider className="border-theme-gray-700 my-2" />

                          <div className="space-y-1">
                            <p className="mb-1">
                              Then overtime amount this month:
                            </p>
                            <p>= (basic salary / 30 / 8) × Multiplier ×</p>
                            <p className="pl-4">This month overtime in hour</p>
                            <p>= (8000 / 30 / 8) × 2 × 5</p>
                            <p>= 33.33 × 2 × 5</p>
                            <p>= 66.66 × 5</p>
                            <p>= 333.33</p>
                          </div>

                          <p className="mt-2 font-medium">
                            Overtime amount this month: 333.33
                          </p>
                        </div>
                      }
                    >
                      <span className="text-theme-primary">
                        <InfoIcon size={16} />
                      </span>
                    </Tooltip>
                  </Flex>
                  <Switch
                    className="custom-switch"
                    checked={amountType === 'percentage'}
                    onChange={(checked) => {
                      if (checked) {
                        form.setFieldValue('type', 'percentage');
                      }
                    }}
                  />
                </Flex>
              }
            >
              <Space.Compact className="w-full items-center border border-theme-slate-700 rounded-md pr-1 bg-theme-slate-800">
                <Form.Item name={'overtime_rate_per_hour_percent'} noStyle>
                  <InputNumber
                    max={100}
                    precision={2}
                    suffix="%"
                    controls={false}
                    className="w-full !border-none"
                    min={amountType === 'percentage' ? 1 : 0}
                    disabled={amountType !== 'percentage' || type === 'VIEW'}
                  />
                </Form.Item>

                <Form.Item noStyle name={'overtime_rate_per_hour_percent_of'}>
                  <Select
                    size="small"
                    placeholder="Select"
                    disabled={amountType !== 'percentage' || type === 'VIEW'}
                    className="!w-[160px] !border-none compact-select"
                    suffixIcon={<ChevronDownIcon size={20} />}
                    placement="bottomRight"
                    onChange={() => {
                      form.setFields([
                        {
                          name: 'overtime_rate_per_hour_percent_of',
                          errors: [],
                        },
                      ]);
                    }}
                    options={[
                      ...(salaryStructureData?.data?.salary_structures?.map(
                        (structure: any) => ({
                          value: structure.component_name,
                          label: snakeToCamel(structure.component_name),
                        }),
                      ) || []),
                      { value: 'gross', label: 'Gross Salary' },
                    ]}
                  />
                </Form.Item>
              </Space.Compact>
            </Form.Item>
            <Form.Item
              layout="horizontal"
              label="Over Time Enabled"
              name={'is_overtime_enabled'}
              initialValue={true}
              className="border-b border-t !mb-0 !pb-0 border-b-theme-slate-700 border-t-theme-slate-700 mt-3 horizontal-end"
            >
              <Switch className="custom-switch" />
            </Form.Item>
            <Form.Item
              label="Status"
              name={'status'}
              layout="horizontal"
              initialValue={true}
              className="border-b border-t !mb-0 !pb-0 border-b-theme-slate-700 border-t-theme-slate-700 mt-3 horizontal-end"
            >
              <Switch className="custom-switch" />
            </Form.Item>
          </div>
          {type !== 'VIEW' && (
            <Flex justify="end">
              <Button
                htmlType="submit"
                className="btn-primary min-w-[80px] h-11 font-semibold"
                loading={id ? isUpdating : isCreating}
              >
                Save
              </Button>
            </Flex>
          )}
        </Form>
      </WithLoader>
    </CustomDrawer>
  );
};

export default ManageOvertimePolicy;
